import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { Layout, Menu, Spin, message, Button, Drawer, Grid, Tag, Card, Alert, Typography } from 'antd';
import {
  DashboardOutlined,
  GlobalOutlined,
  LockOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  CodeOutlined,
  ColumnHeightOutlined,
  ApiOutlined,
  BarChartOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  RocketOutlined,
  MenuOutlined,
  CrownOutlined,
  MedicineBoxOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import { billingService } from '../../services/billingService';
import apiClient from '../../../shared/services/apiClient';
import RenewAction from '../../../shared/components/RenewAction';
import { OwnershipTxtRecord } from '../../../shared/components/OwnershipTxt';

import { userPath } from '../../../shared/config/paths';

const { Content, Sider } = Layout;
const { useBreakpoint } = Grid;

export default function DomainDetail() {
  const { t } = useTranslation();
  const { domainName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const [loading, setLoading] = useState(true);
  const [domainData, setDomainData] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [switchChecking, setSwitchChecking] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [planBilling, setPlanBilling] = useState(null); // 付费套餐的续费信息（免费版为 null）
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 判断是否为移动端（小于 md 断点）
  const isMobile = !screens.md;

  // 菜单项配置
  const menuItems = [
    {
      key: 'overview',
      icon: <DashboardOutlined />,
      label: t('Overview'),
      path: ''
    },
    {
      key: 'origin',
      icon: <GlobalOutlined />,
      label: t('Origin settings'),
      path: '/origin'
    },
    {
      key: 'ssl',
      icon: <LockOutlined />,
      label: 'SSL/TLS',
      path: '/ssl'
    },
    {
      key: 'firewall',
      icon: <SafetyOutlined />,
      label: t('Firewall'),
      path: '/firewall'
    },
    {
      key: 'rules',
      icon: <ColumnHeightOutlined />,
      label: t('Page rules'),
      path: '/rules'
    },
    {
      key: 'cache',
      icon: <DatabaseOutlined />,
      label: t('Cache'),
      path: '/cache'
    },
    {
      key: 'optimization',
      icon: <ThunderboltOutlined />,
      label: t('Optimization'),
      path: '/optimization'
    },
    {
      key: 'network',
      icon: <ApiOutlined />,
      label: t('Network'),
      path: '/network'
    },
    {
      key: 'traffic',
      icon: <BarChartOutlined />,
      label: t('Traffic analytics'),
      path: '/traffic'
    },
    {
      key: 'worker',
      icon: <CodeOutlined />,
      label: 'Worker',
      path: '/worker'
    },
    {
      key: 'plan',
      icon: <CrownOutlined />,
      label: t('Plan'),
      path: '/plan'
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('Settings'),
      path: '/settings'
    },
    {
      key: 'diagnostics',
      icon: <MedicineBoxOutlined />,
      label: t('Diagnostics'),
      path: '/diagnostics'
    },
    {
      key: 'error-pages',
      icon: <ExclamationCircleOutlined />,
      label: t('Error page'),
      path: '/error-pages'
    }
  ];

  useEffect(() => {
    loadDomainData();
  }, [domainName]);

  // silent:子页面保存后/轮询刷新,不把整页换成加载态(否则子页面和弹窗被卸载)
  const loadDomainData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await cdnService.getDomain(domainName);

      if (response.success) {
        setDomainData(response.data);
        if (response.data?.id) {
          try {
            apiClient.get('/api/user/plan/resource', { params: { type: 'cdn', id: response.data.id } })
              .then((r) => {
                const d = r.data?.success ? r.data.data : null;
                setPlanBilling(d && !d.subscription?.is_free ? d.billing : null);
              })
              .catch(() => setPlanBilling(null));
            const subOverview = await billingService.getSubscriptionOverview('cdn', response.data.id);
            if (subOverview.success && subOverview.data) {
              setSubscriptionInfo(subOverview.data);
              const info = subOverview.data;
              // 只有还没开通完(新订单未付)才回向导;过期/暂停的域名照常进详情,在套餐卡片上续费
              if (info.is_paid_plan && info.status === 'pending') {
                navigate(userPath(`/cdn/domains/new?domain=${encodeURIComponent(domainName)}&step=1`));
                return;
              }
            }
          } catch (e) {
            // fallback to status field check below
          }
        }
        if (response.data.status === 'pending') {
          navigate(userPath(`/cdn/domains/new?domain=${encodeURIComponent(domainName)}`));
          return;
        }
      } else {
        message.error(response.error || t('Failed to load domain information'));
        navigate(userPath('/cdn/domains'));
      }
    } catch (error) {
      console.error('Failed to load domain:', error);
      message.error(t('Failed to load domain information'));
      navigate(userPath('/cdn/domains'));
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = ({ key }) => {
    const item = menuItems.find(i => i.key === key);
    if (item) {
      navigate(userPath(`/cdn/domains/${domainName}${item.path}`));
      // 移动端点击菜单后关闭抽屉
      if (isMobile) {
        setDrawerVisible(false);
      }
    }
  };

  const getCurrentMenuKey = () => {
    const path = location.pathname;
    const basePattern = userPath(`/cdn/domains/${domainName}`);

    if (path === basePattern) return 'overview';

    for (const item of menuItems) {
      if (item.path && path.startsWith(basePattern + item.path)) {
        return item.key;
      }
    }

    return 'overview';
  };

  // 获取当前菜单项的标签
  const getCurrentMenuLabel = () => {
    const currentKey = getCurrentMenuKey();
    const item = menuItems.find(i => i.key === currentKey);
    return item ? item.label : t('Overview');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip={t('Loading domain information...')} />
      </div>
    );
  }

  if (!domainData) {
    return null;
  }

  // 菜单组件
  const menuContent = (
    <Menu
      mode="inline"
      selectedKeys={[getCurrentMenuKey()]}
      onClick={handleMenuClick}
      style={{ borderRight: 0, paddingTop: isMobile ? 0 : 16 }}
      items={menuItems}
    />
  );

  return (
    <div>
      {/* 面包屑导航和操作按钮 */}
      <div style={{
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8
        }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(userPath('/cdn/domains'))}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : t('Back to list')}
          </Button>
          {/* 移动端菜单切换按钮 */}
          {isMobile && (
            <Button
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              size="small"
            >
              {getCurrentMenuLabel()}
            </Button>
          )}
          {/* 全局面包屑已显示 CDN / 加速域名 / 域名 / 子页，这里只保留域名标题 */}
          {!isMobile && (
            <span style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}>{domainName}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            icon={<RocketOutlined />}
            onClick={() => navigate(userPath(`/cdn/plans?domain_id=${domainData?.id}`))}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? t('Upgrade') : t('Upgrade plan')}
          </Button>
        </div>
      </div>

      {/* 移动端显示当前域名 */}
      {isMobile && (
        <div style={{
          marginBottom: 12,
          fontSize: 14,
          color: '#666',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>{t('Domain: ')}<strong>{domainName}</strong>
        </div>
      )}

      {/* 套餐信息卡片 */}
      {subscriptionInfo?.has_subscription && (
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          styles={{ body: { padding: isMobile ? '12px' : '12px 24px' } }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <CrownOutlined style={{ color: '#faad14' }} />
                <strong>{subscriptionInfo.product_name}</strong>
                <Tag color={subscriptionInfo.status === 'active' ? 'success' : 'warning'}>
                  {subscriptionInfo.status === 'active' ? t('Taking effect') : subscriptionInfo.status === 'suspended' ? t('Suspended') : subscriptionInfo.status}
                </Tag>
              </span>
              {planBilling && <RenewAction billing={planBilling} resourceName={domainName} />}
              {subscriptionInfo.billing_cycle && (
                <Tag color="blue">
                  {subscriptionInfo.billing_cycle === 'monthly' ? t('Monthly billing') :
                   subscriptionInfo.billing_cycle === 'quarterly' ? t('Quarterly') :
                   subscriptionInfo.billing_cycle === 'annually' ? t('Yearly') : subscriptionInfo.billing_cycle}
                </Tag>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="small"
                icon={<RocketOutlined />}
                onClick={() => navigate(userPath(`/cdn/plans?domain_id=${domainData?.id}`))}
              >{t('Upgrade')}</Button>
              <Button size="small" onClick={() => navigate(userPath(`/cdn/domains/${domainName}/plan`))}>{t('Plan')}</Button>
            </div>
          </div>
        </Card>
      )}
      {!subscriptionInfo?.has_subscription && (
        <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: isMobile ? '12px' : '12px 24px' } }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span><CrownOutlined style={{ color: '#faad14', marginRight: 6 }} />{domainData.plan?.name || <Typography.Text type="secondary">{t('No plan')}</Typography.Text>}</span>
            <Button size="small" type="primary" onClick={() => navigate(userPath(domainData.plan ? `/cdn/domains/${domainName}/plan` : `/cdn/plans?domain_id=${domainData.id}`))}>{domainData.plan ? t('Plan') : t('Select a plan')}</Button>
          </div>
        </Card>
      )}
      {domainData.cname_switch && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={<span>{t('Please change the CNAME to ')}<Typography.Text code copyable>{domainData.cname_switch.to}</Typography.Text>{t(', the old address remains available until the change is complete')}</span>}
          action={
            <Button size="small" loading={switchChecking} onClick={async () => {
              setSwitchChecking(true);
              const r = await cdnService.confirmCnameSwitch(domainData.id);
              setSwitchChecking(false);
              if (r.success) { message.success(t('Switched')); loadDomainData(true); } else message.warning(r.error || t('Not yet effective'));
            }}>{t('I\'ve updated it')}</Button>
          }
        />
      )}

      {domainData.verified === false && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={<span><Tag color="orange">{t('Pending verification')}</Tag>{domainData.verify_deadline ? t('Removed on {{date}} if not verified', { date: String(domainData.verify_deadline).slice(0, 10) }) : null}</span>}
          description={<OwnershipTxtRecord name={domainData.txt_name} value={domainData.txt_value} />}
          action={
            <Button size="small" loading={verifying} onClick={async () => {
              setVerifying(true);
              const r = await cdnService.verifyDomain(domainData.id);
              setVerifying(false);
              if (r.success && r.data?.verified) { message.success(t('Verified')); loadDomainData(true); } else message.warning(r.error || t('Not verified yet'));
            }}>{t('Verify now')}</Button>
          }
        />
      )}

      <Layout style={{ background: '#fff', minHeight: isMobile ? 'auto' : '600px' }}>
        {/* 桌面端侧边栏 */}
        {!isMobile && (
          <Sider
            width={200}
            style={{
              background: '#fff',
              borderRight: '1px solid #f0f0f0'
            }}
          >
            <div style={{ position: 'sticky', top: 72, maxHeight: 'calc(100vh - 88px)', overflowY: 'auto' }}>
              {menuContent}
            </div>
          </Sider>
        )}

        {/* 移动端抽屉菜单 */}
        <Drawer
          title={t('Navigation menu')}
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={250}
          styles={{ body: { padding: 0 } }}
        >
          {menuContent}
        </Drawer>

        {/* 主内容区域 */}
        <Content style={{
          padding: isMobile ? '12px 0' : '0 24px',
          minHeight: 280
        }}>
          <Outlet context={{ domainData, subscriptionInfo, reloadDomain: () => loadDomainData(true) }} />
        </Content>
      </Layout>
    </div>
  );
}
